import React, { createContext, useContext, useMemo } from "react";
import { type ComplianceCaseTypeEnum, type ProfileType } from "~/graphql/supergraphTypes";
import { BankDetailsProfileType } from "../types";

type PaymentInstructionsContextValue = {
  caseId: number;
  caseType: ComplianceCaseTypeEnum;
  programId: number;
  readonly: boolean;
  paymentPrefId?: number;
  paymentAccountId?: number;
  investor: ProfileType;
  profiles: ProfileType[];
  paymentDataProfiles: BankDetailsProfileType[];
};

const PaymentInstructionsContext = createContext<PaymentInstructionsContextValue | undefined>(
  undefined,
);

type PaymentInstructionsProviderProps = {
  caseId: number;
  caseType: ComplianceCaseTypeEnum;
  programId: number;
  readonly: boolean;
  paymentPrefId?: number;
  paymentAccountId?: number;
  investor: ProfileType;
  profiles: ProfileType[];
  children: React.ReactNode;
  paymentDataProfiles: BankDetailsProfileType[];
};

export const PaymentInstructionsProvider: React.FC<PaymentInstructionsProviderProps> = ({
  caseId,
  caseType,
  programId,
  readonly,
  paymentPrefId,
  paymentAccountId,
  children,
  investor,
  profiles,
  paymentDataProfiles,
}) => {
  const contextValue = useMemo(
    (): PaymentInstructionsContextValue => ({
      caseId,
      caseType,
      programId,
      readonly,
      paymentPrefId,
      paymentAccountId,
      investor,
      profiles,
      paymentDataProfiles,
    }),
    [
      caseId,
      caseType,
      programId,
      readonly,
      paymentPrefId,
      paymentAccountId,
      investor,
      profiles,
      paymentDataProfiles,
    ],
  );

  return (
    <PaymentInstructionsContext.Provider value={contextValue}>
      {children}
    </PaymentInstructionsContext.Provider>
  );
};

export const usePaymentInstructions = (): PaymentInstructionsContextValue => {
  const context = useContext(PaymentInstructionsContext);
  if (!context) {
    throw new Error("usePaymentInstructions must be used within a PaymentInstructionsProvider");
  }
  return context;
};
